/**
 * Geo-banner (Brief A — docs/briefs/markets-2026-08/brief-a-geo-banner-hreflang.md)
 *
 * Click-through suggestion for out-of-market visitors. Never redirects, never
 * blocks. Route types: default = link to another store; "localize" = button that
 * switches the country context on this site (EU duties-paid route, 14 Oct 2026). Config comes from the #GeoBannerConfig JSON emitted by
 * snippets/geo-banner.liquid; mechanism documented in docs/geo-banner-hreflang.md.
 * Cross-model review fixes 2026-08-11 (Codex): feature guard, documented
 * endpoint form, sticky-bar offset, CSS-load-gated reveal.
 *
 * Guarantees:
 * - Nothing runs before window load (except with an explicit ?geo_preview override),
 *   and the only network call is the same-origin browsing_context_suggestions.json
 *   fetch — no third-party geo API, nothing on the critical path.
 * - Non-matched visitors get no DOM, no CSS, no layout shift, no flash.
 * - The banner never covers the mobile purchase controls: it offsets itself above
 *   any visible bottom-docked sticky bar (.sticky-mobile-cta / .pdp-sticky-bar).
 * - navigator.webdriver / bot user agents are never interrupted (unless overridden
 *   via ?geo_preview=XX, which is the VPN-free verification hook).
 * - The banner reveals only after its stylesheet has loaded (position:fixed is
 *   also set inline as a belt) — a slow or failed CSS fetch aborts the banner
 *   for that pageview instead of flashing unstyled content.
 * - Dismissal (and CTA click) is remembered for ~30 days via localStorage.
 *   ?geo_preview=clear resets it.
 */
(function () {
  'use strict';

  // Old-browser guard: every API below must exist or we exit before touching
  // anything. This script rides every page of two live stores.
  if (!window.fetch || !window.URLSearchParams || !window.Promise || !window.requestAnimationFrame || !window.sessionStorage) {
    return;
  }

  var root = document.getElementById('GeoBanner');
  var configEl = document.getElementById('GeoBannerConfig');
  if (!root || !configEl) return;

  var config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (error) {
    return;
  }

  var STORAGE_KEY = 'progurt:geo-banner:dismissed-at';
  var COUNTRY_KEY = 'progurt:geo-banner:country';
  var DISMISS_TTL = 30 * 24 * 60 * 60 * 1000; // ~30 days

  var override = '';
  try {
    var params = new URLSearchParams(window.location.search);
    override = (params.get('geo_preview') || '').toUpperCase();
  } catch (error) {
    /* malformed query — treat as no override */
  }

  if (override === 'CLEAR') {
    try {
      localStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(COUNTRY_KEY);
    } catch (error) {
      /* storage unavailable */
    }
    return;
  }

  // Dismissal is honored even in preview mode so persistence is verifiable.
  try {
    var dismissedAt = Number(localStorage.getItem(STORAGE_KEY));
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_TTL) return;
  } catch (error) {
    /* storage unavailable — treat as not dismissed */
  }

  if (!override) {
    if (!config.active) return; // dormant on the AU store
    if (navigator.webdriver) return; // automated agents / agent checkouts
    if (/bot|crawler|spider|crawling|slurp|bingpreview|headlesschrome|lighthouse|gptbot|claudebot|perplexitybot|applebot|petalbot|yandex|duckduckbot/i.test(navigator.userAgent)) {
      return;
    }
  }

  function detectCountry() {
    if (override) return Promise.resolve(override);
    try {
      var cached = sessionStorage.getItem(COUNTRY_KEY);
      if (cached) return Promise.resolve(cached);
    } catch (error) {
      /* storage unavailable */
    }
    // Shopify's first-party IP-based suggestion endpoint (same origin) — the
    // documented request form, rooted for locale subfolders (Brief C).
    var routesRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return window
      .fetch(routesRoot + 'browsing_context_suggestions.json?country%5Benabled%5D=true', {
        headers: { Accept: 'application/json' }
      })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (data) {
        var country =
          data && data.detected_values && data.detected_values.country && data.detected_values.country.handle;
        if (country) {
          try {
            sessionStorage.setItem(COUNTRY_KEY, country);
          } catch (error) {
            /* storage unavailable */
          }
        }
        return country || null;
      })
      .catch(function () {
        return null;
      });
  }

  function matchRoute(country) {
    if (!country || !Array.isArray(config.routes)) return null;
    for (var i = 0; i < config.routes.length; i++) {
      var route = config.routes[i];
      if (route.countries.indexOf(country) === -1) continue;
      // Same-site "localize" routes only speak to visitors still in the default
      // context (onlyFrom) - never to someone who already chose a country.
      if (route.type === 'localize' && route.onlyFrom && config.currentCountry !== route.onlyFrom) return null;
      return route;
    }
    return null;
  }

  // "localize" routes: switch the country context on this site through Shopify's
  // own localization form (POST, then Shopify returns to the same page). No other
  // store, no redirect without a click. The form action is read from the theme's
  // rendered country selector so locale subfolders stay correct.
  function submitLocalization(country) {
    var existing = document.querySelector('form.localization-form');
    var action = (existing && existing.getAttribute('action')) || '/localization';
    var returnTo = window.location.pathname;
    try {
      var params = new URLSearchParams(window.location.search);
      params.delete('geo_preview');
      params.delete('country');
      var query = params.toString();
      if (query) returnTo += '?' + query;
    } catch (error) {
      /* keep the bare path */
    }
    var form = document.createElement('form');
    form.method = 'post';
    form.action = action;
    form.acceptCharset = 'UTF-8';
    form.style.display = 'none';
    var fields = { form_type: 'localization', utf8: '✓', _method: 'put', country_code: country, return_to: returnTo };
    Object.keys(fields).forEach(function (name) {
      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = fields[name];
      form.appendChild(input);
    });
    document.body.appendChild(form);
    form.submit();
  }

  function rememberDismissal() {
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch (error) {
      /* storage unavailable */
    }
  }

  // Never cover the purchase controls: measure visible bottom-docked sticky
  // bars and keep the banner above the tallest one. Re-run on scroll/resize
  // because pdp-sticky-bar toggles with scroll position.
  function stickyOffset() {
    var offset = 0;
    var bars = document.querySelectorAll('.sticky-mobile-cta, .pdp-sticky-bar');
    for (var i = 0; i < bars.length; i++) {
      var rect = bars[i].getBoundingClientRect();
      if (rect.height === 0) continue;
      var style = window.getComputedStyle(bars[i]);
      if (style.display === 'none' || style.visibility === 'hidden') continue;
      if (style.position !== 'fixed' && style.position !== 'sticky') continue;
      // Docked at (or near) the viewport bottom right now?
      if (rect.bottom >= window.innerHeight - 4 && rect.top < window.innerHeight) {
        offset = Math.max(offset, window.innerHeight - rect.top);
      }
    }
    return Math.ceil(offset);
  }

  function trackStickyBars(banner) {
    var scheduled = false;
    var trailing = null;
    function apply() {
      scheduled = false;
      banner.style.bottom = stickyOffset() + 'px';
    }
    function schedule() {
      if (!scheduled) {
        scheduled = true;
        window.requestAnimationFrame(apply);
      }
      // The sticky bars slide in via a CSS transition that finishes after the
      // scroll events stop — re-measure once more when they've settled.
      if (trailing) window.clearTimeout(trailing);
      trailing = window.setTimeout(apply, 450);
    }
    apply();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    // The PDP bar also changes height on click (collapsed <-> expanded) with
    // no scroll event — observe the bars' boxes directly where supported.
    var resizeObserver = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(schedule);
      var bars = document.querySelectorAll('.sticky-mobile-cta, .pdp-sticky-bar');
      for (var i = 0; i < bars.length; i++) resizeObserver.observe(bars[i]);
    }
    return function cleanup() {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      if (trailing) window.clearTimeout(trailing);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }

  // Build "…{country}…{destHost}…" as DOM nodes (host bolded), no innerHTML.
  function buildMessage(route, country) {
    var message = document.createElement('p');
    message.className = 'geo-banner__message';
    var countryName = (route.countryNames && route.countryNames[country]) || country;
    var destHost = (route.dest || '').replace(/^https?:\/\/(www\.)?/, '').replace(/\/.*$/, '');
    var parts = route.message.split(/(\{country\}|\{destHost\})/);
    parts.forEach(function (part) {
      if (part === '{country}') {
        message.appendChild(document.createTextNode(countryName));
      } else if (part === '{destHost}') {
        var strong = document.createElement('strong');
        strong.textContent = destHost;
        message.appendChild(strong);
      } else if (part) {
        message.appendChild(document.createTextNode(part));
      }
    });
    return message;
  }

  function show(route, country) {
    var banner = document.createElement('aside');
    banner.className = 'geo-banner';
    banner.setAttribute('role', 'complementary');
    banner.setAttribute('aria-label', route.regionLabel);
    banner.setAttribute('lang', route.lang);
    // Belt: fixed positioning inline so the element can never join document
    // flow (and thus never shift layout), whatever happens to the stylesheet.
    banner.style.position = 'fixed';
    banner.style.left = '0';
    banner.style.right = '0';
    banner.style.bottom = '0';

    var inner = document.createElement('div');
    inner.className = 'geo-banner__inner';
    inner.appendChild(buildMessage(route, country));

    var actions = document.createElement('div');
    actions.className = 'geo-banner__actions';

    var cta;
    var countryLabel = (route.countryNames && route.countryNames[country]) || country;
    if (route.type === 'localize') {
      cta = document.createElement('button');
      cta.type = 'button';
      cta.className = 'geo-banner__cta';
      cta.textContent = route.cta.replace('{country}', countryLabel);
      cta.addEventListener('click', function () {
        rememberDismissal(); // switched once — don't nag on return
        submitLocalization(country);
      });
    } else {
      cta = document.createElement('a');
      cta.className = 'geo-banner__cta';
      cta.href = route.utm ? route.dest + '/?' + route.utm : route.dest;
      cta.rel = 'noopener';
      cta.textContent = route.cta;
      cta.addEventListener('click', rememberDismissal); // routed once — don't nag on return
    }

    var dismiss = document.createElement('button');
    dismiss.type = 'button';
    dismiss.className = 'geo-banner__dismiss';
    dismiss.textContent = route.dismiss;
    dismiss.addEventListener('click', function () {
      rememberDismissal();
      banner.classList.remove('geo-banner--visible');
      window.setTimeout(function () {
        banner.remove();
        if (stopTracking) stopTracking(); // release scroll/resize listeners + observer
      }, 400);
    });

    actions.appendChild(cta);
    actions.appendChild(dismiss);
    inner.appendChild(actions);
    banner.appendChild(inner);

    // Reveal ONLY once the component stylesheet is confirmed loaded — a slow
    // or failed fetch aborts the banner for this pageview rather than ever
    // showing unstyled content.
    var settled = false;
    var stopTracking = null;
    function reveal() {
      if (settled) return;
      settled = true;
      root.appendChild(banner);
      stopTracking = trackStickyBars(banner);
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          banner.classList.add('geo-banner--visible'); // fade only, no translate
        });
      });
    }
    function abort() {
      settled = true;
    }

    var stylesheet = document.createElement('link');
    stylesheet.rel = 'stylesheet';
    stylesheet.href = config.cssHref;
    stylesheet.addEventListener('load', reveal);
    stylesheet.addEventListener('error', abort);
    document.head.appendChild(stylesheet);
    window.setTimeout(function () {
      // Cached-CSS fallback: onload can be missed, but a parsed sheet proves
      // the CSS is live. No sheet after 1.5s -> give up quietly.
      if (!settled) {
        if (stylesheet.sheet) reveal();
        else abort();
      }
    }, 1500);
  }

  function start() {
    detectCountry().then(function (country) {
      var route = matchRoute(country);
      if (route) show(route, country);
    });
  }

  if (override || document.readyState === 'complete') {
    start();
  } else {
    window.addEventListener('load', start, { once: true });
  }
})();
